import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Trash2, ShoppingCart, Loader2, CheckCircle, CreditCard, Lock } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session ID for abandoned cart tracking
function getSessionId() {
  let sid = sessionStorage.getItem('cart_session_id');
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('cart_session_id', sid);
  }
  return sid;
}

function trackCart(cartItems, stage, customerInfo = {}) {
  if (cartItems.length === 0) return;
  const data = {
    session_id: getSessionId(),
    items: cartItems.map(item => ({
      product_id: item.id,
      name: item.name,
      tonnage: item.tonnage,
      map_price: item.map_price,
      sale_price: item.sale_price,
      quantity: item.quantity || 1,
    })),
    stage,
    customer_name: customerInfo.name || null,
    customer_email: customerInfo.email || null,
    customer_phone: customerInfo.phone || null,
    customer_address: customerInfo.address || null,
    county: customerInfo.county || null,
  };
  axios.post(`${API}/abandoned-cart`, data).catch(() => {});
}

const COUNTIES = [
  "Brevard County",
  "Flagler County",
  "Lake County",
  "Marion County",
  "Orange County",
  "Osceola County",
  "Polk County",
  "Putnam County",
  "Seminole County",
  "Sumter County",
  "Volusia County",
];

export const CartModal = ({ isOpen, onClose, cartItems, setCartItems }) => {
  const [step, setStep] = useState('cart');
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentConfigured, setPaymentConfigured] = useState(false);
  const [taxInfo, setTaxInfo] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    county: ''
  });
  const [cardInfo, setCardInfo] = useState({
    card_number: '',
    exp_month: '',
    exp_year: '',
    cvv: '',
    card_holder_name: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payment/status`);
        setPaymentConfigured(response.data.configured);
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    };
    if (isOpen) {
      checkPaymentStatus();
      // Track abandoned cart - stage: cart
      if (cartItems.length > 0) {
        trackCart(cartItems, 'cart');
      }
    }
  }, [isOpen]);

  // Calculate tax when county changes
  useEffect(() => {
    if (!customerInfo.county || cartItems.length === 0) {
      setTaxInfo(null);
      return;
    }
    const fetchTax = async () => {
      setTaxLoading(true);
      try {
        const res = await axios.post(`${API}/tax/calculate`, {
          items: cartItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
          county: customerInfo.county
        });
        setTaxInfo(res.data);
      } catch (err) {
        console.error('Tax calc error:', err);
        setTaxInfo(null);
      } finally {
        setTaxLoading(false);
      }
    };
    fetchTax();
  }, [customerInfo.county, cartItems]);

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(price);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Detect AMEX (starts with 34 or 37)
    if (v.startsWith('34') || v.startsWith('37')) {
      // AMEX format: xxxx xxxxxx xxxxx
      const parts = [v.slice(0, 4), v.slice(4, 10), v.slice(10, 15)].filter(Boolean);
      return parts.join(' ');
    }
    // Default: xxxx xxxx xxxx xxxx
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.sale_price * item.quantity), 0);

  const removeItem = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
    toast.success('Item removed from cart');
  };

  const validateCheckout = () => {
    const newErrors = {};
    if (!customerInfo.name.trim()) newErrors.name = 'Name is required';
    if (!customerInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) newErrors.email = 'Invalid email';
    if (!customerInfo.phone.trim()) newErrors.phone = 'Phone is required';
    if (!customerInfo.address.trim()) newErrors.address = 'Address is required';
    if (!customerInfo.county) newErrors.county = 'County is required for tax calculation';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePayment = () => {
    const newErrors = {};
    const cardNum = cardInfo.card_number.replace(/\s/g, '');
    if (!cardNum || cardNum.length < 15) newErrors.card_number = 'Valid card number required';
    if (!cardInfo.exp_month || cardInfo.exp_month.length !== 2) newErrors.exp_month = 'Required';
    if (!cardInfo.exp_year || cardInfo.exp_year.length !== 2) newErrors.exp_year = 'Required';
    if (!cardInfo.cvv || cardInfo.cvv.length < 3) newErrors.cvv = 'CVV required';
    if (!cardInfo.card_holder_name.trim()) newErrors.card_holder_name = 'Cardholder name required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async () => {
    if (!validatePayment()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payment/process`, {
        items: cartItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        customer_address: customerInfo.address,
        county: customerInfo.county,
        card: {
          card_number: cardInfo.card_number.replace(/\s/g, ''),
          exp_month: cardInfo.exp_month,
          exp_year: cardInfo.exp_year,
          cvv: cardInfo.cvv,
          card_holder_name: cardInfo.card_holder_name
        }
      });
      if (response.data.success) {
        setOrderDetails(response.data);
        setStep('success');
        setCartItems([]);
        toast.success('Payment successful!');
      } else {
        toast.error(response.data.message || 'Payment failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Payment processing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const grandTotal = taxInfo ? taxInfo.grand_total : subtotal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cart-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step === 'payment' ? (
              <CreditCard className="w-5 h-5 text-emerald-600" />
            ) : (
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
            )}
            <h2 className="font-bold text-lg text-gray-900">
              {step === 'cart' && 'Your Cart'}
              {step === 'checkout' && 'Customer Information'}
              {step === 'payment' && 'Payment'}
              {step === 'success' && 'Order Confirmed'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="cart-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* ===== CART STEP ===== */}
          {step === 'cart' && (
            <>
              {cartItems.length === 0 ? (
                <div className="text-center py-12" data-testid="cart-empty">
                  <ShoppingCart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg" data-testid={`cart-item-${item.id}`}>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{item.name}</h4>
                          <p className="text-xs text-gray-500">{item.tonnage}</p>
                          <div className="mt-1.5">
                            <span className="text-gray-400 line-through text-sm mr-2">{formatPrice(item.map_price)}</span>
                            <span className="font-bold text-emerald-600 text-lg">{formatPrice(item.sale_price)}</span>
                          </div>
                          <span className="text-emerald-600 font-semibold text-xs bg-emerald-100 px-2 py-0.5 rounded-full mt-1 inline-block">
                            You Save {formatPrice(item.map_price - item.sale_price)} ({Math.round((item.map_price - item.sale_price) / item.map_price * 100)}% Off)
                          </span>
                        </div>
                        <button onClick={() => removeItem(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-white rounded-full transition-colors" data-testid={`cart-remove-${item.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 pt-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Subtotal</span>
                      <span className="font-bold text-2xl text-emerald-600" data-testid="cart-total">{formatPrice(subtotal)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Tax calculated at checkout based on delivery county</p>
                  </div>

                  <button onClick={() => setStep('checkout')} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors" data-testid="cart-continue-btn">
                    Continue to Checkout
                  </button>
                </>
              )}
            </>
          )}

          {/* ===== CHECKOUT STEP ===== */}
          {step === 'checkout' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="checkout-name" className="text-sm font-medium text-gray-700">Full Name *</Label>
                <Input id="checkout-name" value={customerInfo.name} onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))} className={`mt-1.5 rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-200'}`} placeholder="John Smith" data-testid="checkout-name" />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="checkout-email" className="text-sm font-medium text-gray-700">Email *</Label>
                <Input id="checkout-email" type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))} className={`mt-1.5 rounded-lg ${errors.email ? 'border-red-500' : 'border-gray-200'}`} placeholder="john@example.com" data-testid="checkout-email" />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="checkout-phone" className="text-sm font-medium text-gray-700">Phone *</Label>
                <Input id="checkout-phone" type="tel" value={customerInfo.phone} onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))} className={`mt-1.5 rounded-lg ${errors.phone ? 'border-red-500' : 'border-gray-200'}`} placeholder="(555) 123-4567" data-testid="checkout-phone" />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div>
                <Label htmlFor="checkout-address" className="text-sm font-medium text-gray-700">Delivery Address *</Label>
                <Input id="checkout-address" value={customerInfo.address} onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))} className={`mt-1.5 rounded-lg ${errors.address ? 'border-red-500' : 'border-gray-200'}`} placeholder="123 Main St, City, FL 32901" data-testid="checkout-address" />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>

              <div>
                <Label htmlFor="checkout-county" className="text-sm font-medium text-gray-700">Delivery County *</Label>
                <select
                  id="checkout-county"
                  value={customerInfo.county}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, county: e.target.value }))}
                  className={`mt-1.5 w-full rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.county ? 'border-red-500' : 'border-gray-200'}`}
                  data-testid="checkout-county"
                >
                  <option value="">Select your county...</option>
                  {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.county && <p className="text-red-500 text-xs mt-1">{errors.county}</p>}
              </div>

              {/* Tax Preview */}
              {customerInfo.county && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-testid="tax-preview">
                  {taxLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" /> Calculating tax...
                    </div>
                  ) : taxInfo ? (
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal</span>
                        <span>{formatPrice(taxInfo.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>FL State Tax (6%)</span>
                        <span>{formatPrice(taxInfo.state_tax)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{customerInfo.county} Surtax ({(taxInfo.surtax_rate * 100).toFixed(1)}%)</span>
                        <span>{formatPrice(taxInfo.county_surtax)}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-1.5 flex justify-between font-bold text-gray-900">
                        <span>Total</span>
                        <span className="text-emerald-600">{formatPrice(taxInfo.grand_total)}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button onClick={() => setStep('cart')} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors" data-testid="checkout-back-btn">
                  Back
                </button>
                <button
                  onClick={() => {
                    if (validateCheckout()) {
                      trackCart(cartItems, 'checkout', customerInfo);
                      setStep('payment');
                    }
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  Continue to Payment
                </button>
              </div>
            </div>
          )}

          {/* ===== PAYMENT STEP ===== */}
          {step === 'payment' && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
                <Lock className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-gray-600">Secure payment powered by Authorize.net</span>
              </div>

              {!paymentConfigured && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <p className="text-sm text-yellow-800"><strong>Demo Mode:</strong> Payment gateway not configured. Using test mode.</p>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-700">Card Number *</Label>
                <Input value={cardInfo.card_number} onChange={(e) => setCardInfo(prev => ({ ...prev, card_number: formatCardNumber(e.target.value) }))} className={`mt-1.5 rounded-lg ${errors.card_number ? 'border-red-500' : 'border-gray-200'}`} placeholder="4111 1111 1111 1111" maxLength={19} data-testid="card-number" />
                {errors.card_number && <p className="text-red-500 text-xs mt-1">{errors.card_number}</p>}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Month *</Label>
                  <Input value={cardInfo.exp_month} onChange={(e) => setCardInfo(prev => ({ ...prev, exp_month: e.target.value.replace(/\D/g, '').slice(0, 2) }))} className={`mt-1.5 rounded-lg ${errors.exp_month ? 'border-red-500' : 'border-gray-200'}`} placeholder="MM" maxLength={2} data-testid="card-exp-month" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Year *</Label>
                  <Input value={cardInfo.exp_year} onChange={(e) => setCardInfo(prev => ({ ...prev, exp_year: e.target.value.replace(/\D/g, '').slice(0, 2) }))} className={`mt-1.5 rounded-lg ${errors.exp_year ? 'border-red-500' : 'border-gray-200'}`} placeholder="YY" maxLength={2} data-testid="card-exp-year" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">CVV *</Label>
                  <Input type="password" value={cardInfo.cvv} onChange={(e) => setCardInfo(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))} className={`mt-1.5 rounded-lg ${errors.cvv ? 'border-red-500' : 'border-gray-200'}`} placeholder="123" maxLength={4} data-testid="card-cvv" />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Cardholder Name *</Label>
                <Input value={cardInfo.card_holder_name} onChange={(e) => setCardInfo(prev => ({ ...prev, card_holder_name: e.target.value }))} className={`mt-1.5 rounded-lg ${errors.card_holder_name ? 'border-red-500' : 'border-gray-200'}`} placeholder="John Smith" data-testid="card-holder-name" />
                {errors.card_holder_name && <p className="text-red-500 text-xs mt-1">{errors.card_holder_name}</p>}
              </div>

              {/* Order Summary with Tax */}
              <div className="border-t border-gray-100 pt-4 mt-6" data-testid="payment-summary">
                <div className="space-y-1.5 text-sm mb-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {taxInfo && (
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>FL State Tax (6%)</span>
                        <span>{formatPrice(taxInfo.state_tax)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{customerInfo.county} Surtax ({(taxInfo.surtax_rate * 100).toFixed(1)}%)</span>
                        <span>{formatPrice(taxInfo.county_surtax)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500 text-xs">
                        <span>Total Tax</span>
                        <span>{formatPrice(taxInfo.total_tax)}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900 text-base">
                    <span>Total to Pay</span>
                    <span className="text-emerald-600">{formatPrice(grandTotal)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('checkout')} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors">
                  Back
                </button>
                <button onClick={handlePayment} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2" data-testid="checkout-confirm-btn">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                  ) : (
                    <><Lock className="w-4 h-4" /> Pay {formatPrice(grandTotal)}</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ===== SUCCESS STEP ===== */}
          {step === 'success' && orderDetails && (
            <div className="text-center py-8" data-testid="checkout-success">
              <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
              <h3 className="font-bold text-2xl text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-6">{orderDetails.message}</p>
              <div className="bg-gray-50 rounded-lg p-4 text-left mb-6 space-y-2">
                <p className="text-sm text-gray-600"><strong>Order ID:</strong> {orderDetails.order_id}</p>
                <p className="text-sm text-gray-600"><strong>Transaction ID:</strong> {orderDetails.transaction_id}</p>
                {orderDetails.authorization_code && (
                  <p className="text-sm text-gray-600"><strong>Auth Code:</strong> {orderDetails.authorization_code}</p>
                )}
                <p className="text-sm text-gray-600"><strong>Total:</strong> {formatPrice(orderDetails.total)}</p>
                <p className="text-sm text-gray-600"><strong>Status:</strong> <span className="text-emerald-600 font-semibold">{orderDetails.status}</span></p>
              </div>
              <button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors" data-testid="checkout-done-btn">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartModal;
